/**
 * A short read for an opened dataset.
 * Levels and ranks are computed from the series. The QXO sentence is written
 * against that rank. Nothing here invents a print the source did not publish.
 */

import { BEACON_LIKE_FOR_LIKE, PLATFORM_BASELINE, QXO_QUARTERS } from '@/components/qxo/qxoFilingsData';
import {
  LONG_BRANCH_COST,
  LONG_CONSTRUCTION_FUNNEL,
  LONG_CONSTRUCTION_LABOR,
  LONG_EXISTING_HOME_SALES,
  LONG_HOUSING_BACKLOG,
  LONG_HOUSING_STARTS,
  LONG_INPUT_PPI,
  LONG_MORTGAGE,
  LONG_NEW_HOME_MOS,
  LONG_PUT_IN_PLACE,
  LONG_REGIONAL_STARTS,
  LONG_SF_FUNNEL,
  LONG_STARTS_VS_SALES,
  LONG_STRUCTURE_STARTS,
} from '@/components/qxo/qxoLongHistory';

export interface SeriesRead {
  where: string;
  heading: string;
  forQxo: string;
}

interface Point {
  label: string;
  value: number;
}

interface Placement {
  latestLabel: string;
  latest: number;
  minLabel: string;
  min: number;
  maxLabel: string;
  max: number;
  percentile: number;
  yoy: number | null;
  lastFourDelta: number | null;
  partial: boolean;
  startLabel: string;
}

function place(points: Point[]): Placement {
  const rows = points.filter((p) => Number.isFinite(p.value));
  const latest = rows[rows.length - 1];
  let min = rows[0];
  let max = rows[0];
  for (const point of rows) {
    if (point.value < min.value) min = point;
    if (point.value > max.value) max = point;
  }
  const sorted = rows.map((p) => p.value).sort((a, b) => a - b);
  const atOrBelow = sorted.filter((value) => value <= latest.value).length;
  const yearAgo = rows.length > 4 ? rows[rows.length - 5] : null;
  const fourAgo = rows.length > 3 ? rows[rows.length - 4] : null;
  return {
    latestLabel: latest.label,
    latest: latest.value,
    minLabel: min.label,
    min: min.value,
    maxLabel: max.label,
    max: max.value,
    percentile: Math.round((atOrBelow / sorted.length) * 100),
    yoy: yearAgo && yearAgo.value !== 0 ? (latest.value / yearAgo.value - 1) * 100 : null,
    lastFourDelta: fourAgo ? latest.value - fourAgo.value : null,
    partial: latest.label.includes('*'),
    startLabel: rows[0].label,
  };
}

function num(value: number, digits: number): string {
  return value.toFixed(digits);
}

function signedPct(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return 'n/a';
  const text = `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
  return text;
}

function band(percentile: number): string {
  if (percentile <= 20) return 'near the bottom of this range';
  if (percentile <= 40) return 'in the lower half of this range';
  if (percentile <= 60) return 'around the middle of this range';
  if (percentile <= 80) return 'in the upper half of this range';
  return 'near the top of this range';
}

function levelSentence(name: string, spot: Placement, digits: number, unit: string): string {
  const partial = spot.partial ? ` ${spot.latestLabel} is not a finished quarter, so the rank can still move.` : '';
  const yoy = spot.yoy == null ? '' : ` Versus the same quarter a year ago it is ${signedPct(spot.yoy)}.`;
  return `${name} in ${spot.latestLabel} is ${num(spot.latest, digits)}${unit}. That is ${band(spot.percentile)} (about the ${spot.percentile}th percentile of quarters back to ${spot.startLabel}). The low was ${num(spot.min, digits)}${unit} in ${spot.minLabel}; the high was ${num(spot.max, digits)}${unit} in ${spot.maxLabel}.${yoy}${partial}`;
}

function headingFromDelta(spot: Placement, up: string, down: string, flat: string): string {
  if (spot.lastFourDelta == null || Math.abs(spot.lastFourDelta) < 0.02) return flat;
  return spot.lastFourDelta > 0 ? up : down;
}

function cycleBackdrop(): string {
  const houses = place(LONG_HOUSING_STARTS.map((row) => ({ label: row.period, value: row.single })));
  const rate = place(LONG_MORTGAGE.map((row) => ({ label: row.period, value: row.rate })));
  const sales = LONG_EXISTING_HOME_SALES[LONG_EXISTING_HOME_SALES.length - 1];
  return `The longer cycle around this print: single-family starts are ${num(houses.latest, 2)} million (${band(houses.percentile)}), the 30-year mortgage averages ${num(rate.latest, 2)}% this quarter (${band(rate.percentile)}), and existing-home sales were ${num(sales.salesM, 2)} million in ${sales.year}.`;
}

const SHORT_QXO: Record<string, string> = {
  'arma-shipments':
    'ARMA squares are the volume Beacon actually sells against. Public quarterly PDFs start in 2022, so this chart cannot honestly go back to 2011. Read the latest year-over-year print against the housing cycle: a flat or down square count while starts are already in the lower half of their range says the mill book is not waiting on a housing recovery to weaken. It is already there.',
  'mills-vs-branch':
    'Mill shipments and branch same-store sales are different perimeters. When ARMA is firmer than Beacon’s organic print, distributors are losing share or cutting inventory even if mills are still shipping. That gap is the QXO question, and it only exists for the quarters both sides published.',
  'oc-roofing':
    'Owens Corning is the largest branded mill selling into branches like Beacon. Dollar sales can rise on price while volume falls. Volume is the line that belongs next to ARMA. A short OC window still tells you whether the mill is describing the same quarter QXO’s branches just lived through.',
  'peer-tape':
    'IBP residential is the public stand-in for TopBuild until QXO reports it. Builders FirstSource is the stand-in for Kodiak. These lines are not the same metric and they do not go back 15 years in this file. A negative residential print from both, in a housing market already off its 2021 peak, is the demand QXO just bought.',
  'oc-channel':
    'This is a call, not a time series. Heavier distributor inventories and flat hail counts push the slowdown toward price, destock, and housing rather than a storm year. That is the setup Beacon’s next same-store print has to beat.',
  'steep-vs-low':
    'Shingles and modified bitumen can diverge inside one ARMA report. Beacon’s steep-slope book is the house line; low-slope is commercial. If shingles are the weak leg, the residential same-store number is the one to watch, not a blended “roofing” comment.',
  'installer-split':
    'TopBuild’s own residential same-branch was the weak leg into the close. IBP is the live proxy after that. House jobs lag single-family completions, which themselves lag starts. A still-negative residential installer print is consistent with starts that have already come off the 2021 high.',
  'bldr-end-market':
    'Kodiak is lumber, not shingles. Builders FirstSource’s repair book holding up better than single-family is the relevant split: complementary product can offset a weak new-construction roof quarter, and it can also hide it.',
  'reported-mix':
    'Three snapshots, not a 15-year mix. The jump in complementary is Kodiak arriving, not Beacon selling more gutters. Do not read the latest bar as an organic mix shift.',
  'beacon-margins':
    'Beacon’s own FY2024 margin was 9.5%, and its best quarter in that year was 11.7%. The 2030 Beacon-platform target needs about 21% on roughly flat sales. The quarters since the close are the only evidence, and they are not on that path yet.',
  'like-for-like':
    'Same-branch economics, not the acquired-company stack. Sales and EBITDA versus the Beacon quarter a year earlier are the cleanest “is the platform getting better” test. Several of those comparisons are still down, which is the opposite of the margin expansion the 2030 plan requires.',
  'qxo-quarters':
    'QXO has only owned Beacon since April 2025. There is no QXO income statement for 2011–2024. Q2 2026 also folds in the first Kodiak quarter, so the sales jump is a perimeter change. Judge the platform on the like-for-like board, not on the reported sales line.',
  'beacon-standalone':
    'This is Beacon before QXO, FY2024 plus the stub. It is the cost and margin base the deal was underwritten on. It is not a cycle history.',
  'product-lines':
    'Product-line disclosure is sparse and the perimeter changes when Kodiak arrives. Use it to see what QXO chose to show, not as a 15-year category trend.',
  'working-capital':
    'Working capital since the close is the cash the branch network ties up. A seasonal build that does not reverse is a problem at this debt load. The series is only as long as QXO has owned the branches.',
  'fcf-vs-pref':
    'Preferred dividends are a fixed cash claim. Free cash flow has to cover them before equity holders see anything. A housing market in the lower half of its range is the environment that cash flow has to be earned in.',
  'proof-points':
    'These are management statements, not a statistical series. Treat them as claims to check against the like-for-like sales and margin prints.',
  'ebitda-waterfall':
    'The waterfall is one year’s pro forma, built after the deals. It is the starting EBITDA the debt and preferred have to be carried by. It is not a forecast.',
  'debt-stack':
    'The stack is the post-TopBuild balance sheet. It did not exist in 2011. What matters is the rate and the maturity wall against EBITDA that is being earned in a slow housing tape, not a 2021 tape.',
  'preferred':
    'Preferred stock sits ahead of the common and its dividend is cash. At a mid-teens common price a large piece of it is still senior. Coverage is the question, and coverage uses today’s EBITDA, not a recovered housing cycle.',
  'dilution-curve':
    'Dilution is a price question. More of the preferred and warrants convert as the stock rises, so the share count the 2030 plan is divided by is not the share count at today’s price.',
  'equity-ladder':
    'The ladder is the issuance record since the Beacon financing. It explains why the share count is what it is. It is not a returns history.',
  'deal-multiples':
    'Each platform was bought at a multiple of the EBITDA it had already earned. The equity story is the gap between that multiple and the 2030 target. The housing cycle says the starting EBITDA was not earned at the peak.',
  platforms:
    'Three platforms, three close dates, all inside two years. There is no combined 15-year record. The 2030 EBITDA targets are management’s, set against FY2025 baselines that were already off the 2021 housing peak.',
  'annual-consensus':
    'Street estimates are a forward path from the latest snapshot, not a history. The comparison that matters is the furthest year the street will print against management’s 2030 EBITDA. If those stay far apart, the multiple is a bet on a plan the models have not adopted.',
  'quarterly-consensus':
    'Near-term quarters are the test. A miss against a modest street bar, in a housing market already in the lower half of its range, is demand. A beat that is only Kodiak or TopBuild entering the perimeter is not.',
  'prints-vs-estimates':
    'This is the recent scorecard of prints versus the estimate that was in the market. It is short because QXO as a roofing company is short. Use it to see whether the street is chasing the company or the company is missing a low bar.',
  'ownership-13f':
    '13F holdings are a positioning snapshot, lagged by the filing calendar. The percentage some feeds show can exceed 100% when the share-count denominator is stale. Use the share count, not that percentage.',
  insiders:
    'Form 4s are the open-market record in the snapshot. A tape with sales and no open-market buys is a fact about recent behavior, not a 15-year insider study.',
  grades:
    'Rating changes are opinions with dates. They belong next to the price and the housing cycle, not instead of the filings.',
  'sec-filings':
    'The filing list is the document trail. The numbers that matter are in the 10-Q and the earnings release, not in the count of filings.',
};

function longReads(): Record<string, SeriesRead> {
  const single = place(LONG_HOUSING_STARTS.map((row) => ({ label: row.period, value: row.single })));
  const total = place(LONG_HOUSING_STARTS.map((row) => ({ label: row.period, value: row.total })));
  const rate = place(LONG_MORTGAGE.map((row) => ({ label: row.period, value: row.rate })));
  const salesPoints = LONG_EXISTING_HOME_SALES.map((row) => ({ label: row.year, value: row.salesM }));
  const sales = place(salesPoints);
  const permits = place(LONG_CONSTRUCTION_FUNNEL.map((row) => ({ label: row.period, value: row.permit })));
  const complete = place(LONG_CONSTRUCTION_FUNNEL.map((row) => ({ label: row.period, value: row.complete })));
  const south = place(LONG_REGIONAL_STARTS.map((row) => ({ label: row.period, value: row.south })));
  const midwest = place(LONG_REGIONAL_STARTS.map((row) => ({ label: row.period, value: row.midwest })));
  const houses = place(LONG_STRUCTURE_STARTS.map((row) => ({ label: row.period, value: row.sf })));
  const apartments = place(LONG_STRUCTURE_STARTS.map((row) => ({ label: row.period, value: row.mf })));
  const sfComplete = place(LONG_SF_FUNNEL.map((row) => ({ label: row.period, value: row.complete })));
  const sfStarts = place(LONG_SF_FUNNEL.map((row) => ({ label: row.period, value: row.starts })));
  const sold = place(LONG_STARTS_VS_SALES.map((row) => ({ label: row.period, value: row.sold })));
  const started = place(LONG_STARTS_VS_SALES.map((row) => ({ label: row.period, value: row.starts })));
  const mos = place(LONG_NEW_HOME_MOS.map((row) => ({ label: row.period, value: row.mos })));
  const under = place(LONG_HOUSING_BACKLOG.map((row) => ({ label: row.period, value: row.under })));
  const notStarted = place(LONG_HOUSING_BACKLOG.map((row) => ({ label: row.period, value: row.notStarted })));
  const resi = place(LONG_PUT_IN_PLACE.map((row) => ({ label: row.period, value: row.resi })));
  const asphalt = place(LONG_INPUT_PPI.map((row) => ({ label: row.period, value: row.asphalt })));
  const shingles = place(LONG_INPUT_PPI.map((row) => ({ label: row.period, value: row.shingles })));
  const lumber = place(LONG_INPUT_PPI.map((row) => ({ label: row.period, value: row.lumber })));
  const diesel = place(LONG_BRANCH_COST.map((row) => ({ label: row.period, value: row.diesel })));
  const freight = place(LONG_BRANCH_COST.map((row) => ({ label: row.period, value: row.freight })));
  const payrolls = place(LONG_CONSTRUCTION_LABOR.map((row) => ({ label: row.period, value: row.empYoy })));

  const gap = started.latest - sold.latest;

  return {
    'housing-starts': {
      where: `${levelSentence('Single-family starts', single, 2, ' million')} Total starts are ${num(total.latest, 2)} million in the same quarter.`,
      heading: headingFromDelta(
        single,
        'House starts have ticked up over the last four quarters. That is a turn off the lows, not a return to the 2021 peak.',
        'House starts are still slipping on a four-quarter view. New-construction orders follow this line down before branch sales do.',
        'House starts have gone sideways for four quarters. Sideways, this far below the peak, is a slow new-build market.',
      ),
      forQxo:
        'Single-family starts are the new-construction order book for Beacon shingles and, after the close, for TopBuild insulation. Repair and reroof, the larger Beacon slice, do not show up here. A print in the lower half of a 15-year range means the new-build book is already slow. A sustained move higher would show up in starts before it shows up in same-store sales.',
    },
    'mortgage-rate': {
      where: levelSentence('The 30-year mortgage, quarterly average', rate, 2, '%'),
      heading: headingFromDelta(
        rate,
        'Rates have drifted up over the last four quarters. That keeps both turnover and new starts tight.',
        'Rates have eased over the last four quarters. Easing from a high level is not the same as the 3% market that produced the 2021 boom.',
        'Rates have been stuck for four quarters. Stuck near the top of this range is the freeze.',
      ),
      forQxo:
        'QXO does not lend. It feels the rate when contractors stop pulling orders. A move back toward 5% would be the first thing that reopens existing-home turnover and discretionary reroof. Staying in the mid-6s keeps the repair book dependent on roof age and storms rather than people moving.',
    },
    'existing-home-sales': {
      where: `Existing-home sales were ${num(sales.latest, 2)} million in ${sales.latestLabel}. That is ${band(sales.percentile)} of the annual totals back to ${sales.startLabel} (about the ${sales.percentile}th percentile). The low was ${num(sales.min, 2)} million in ${sales.minLabel}; the high was ${num(sales.max, 2)} million in ${sales.maxLabel}.`,
      heading:
        sales.latest <= sales.min + 0.15
          ? 'The last two full years are sitting on the floor of this range. That is the post-2022 freeze, not a one-year dip.'
          : 'Annual sales have come off the 2021 peak and have not rebuilt. The monthly SAAR in the table is the live check on whether this year is breaking that floor.',
      forQxo:
        'Turnover is when a lot of reroofs and complementary products get bought. It is a weak proxy by itself — roof age and hail still decide squares — but a market back near 4 million sales says the move-in reroof is not the growth driver while rates stay here.',
    },
    'construction-funnel': {
      where: `${levelSentence('Permits', permits, 2, ' million')} Completions in the same quarter are ${num(complete.latest, 2)} million (${band(complete.percentile)}).`,
      heading: headingFromDelta(
        complete,
        'Finished homes have turned up over four quarters. Installer crews feel completions, and they feel them after starts.',
        'Finished homes are rolling over. That hits jobsites after starts have already slowed, which is the lag TopBuild and IBP live in.',
        'Completions are flat over four quarters. Flat completions mean the install book is not getting a new wave of work.',
      ),
      forQxo:
        'Starts lead shingle and lumber orders. Completions are what installer crews work. If completions are the line falling while permits only look steady, the install platform feels the slowdown on a delay. The next few completion prints matter more for TopBuild than another small move in permits.',
    },
    'regional-starts': {
      where: `${levelSentence('Southern starts', south, 2, ' million')} The Midwest is ${num(midwest.latest, 2)} million in ${midwest.latestLabel} (${band(midwest.percentile)}).`,
      heading:
        south.lastFourDelta != null && south.lastFourDelta < -0.02
          ? 'The South — the storm and Sunbelt roof book — is the region slipping. A national average will look better than Beacon’s branch map if that continues.'
          : 'Watch the South against the Midwest. Owens Corning has described the Midwest as the firmer pocket. The national total hides that split.',
      forQxo:
        'Beacon’s residential book is not a US average. A weak South with a firmer Midwest is a branch-map story. Storm demand can still override a weak start print in the South; it does not rewrite the new-construction trend.',
    },
    'structure-starts': {
      where: `${levelSentence('Single-family starts', houses, 2, ' million')} Apartment starts (5+ units) are ${num(apartments.latest, 2)} million (${band(apartments.percentile)}).`,
      heading: headingFromDelta(
        apartments,
        'Apartments have bounced over four quarters. That is low-slope and a different install job, not steep-slope shingles.',
        'Apartments are still the weaker structure. Houses are the steep-slope book; do not let a stable total-starts number hide a soft house line.',
        'The house-versus-apartment mix has been stable recently. Mix, not just the total, decides which QXO product moves.',
      ),
      forQxo:
        'Houses take steep-slope shingles and a TopBuild insulation package. Apartments take low-slope roofing. If the house line is the one in the lower half of its range, residential same-store is the print under pressure.',
    },
    'sf-funnel': {
      where: `${levelSentence('Single-family starts', sfStarts, 2, ' million')} Finished houses are ${num(sfComplete.latest, 2)} million in the same quarter (${band(sfComplete.percentile)}).`,
      heading: headingFromDelta(
        sfComplete,
        'Finished houses have improved over four quarters. That is the work already reaching jobsites.',
        'Finished houses are falling even if permits look steadier. Crews see the completion line, not the permit line.',
        'Finished houses are roughly flat. The house pipeline is not reaccelerating.',
      ),
      forQxo:
        'This is the housing slice that feeds Beacon residential and TopBuild’s house book. Completions falling faster than starts means less jobsite work even when the permit headline looks fine.',
    },
    'starts-vs-sales': {
      where: `${levelSentence('New homes sold', sold, 2, ' million')} Houses started in the same quarter were ${num(started.latest, 2)} million, a gap of ${num(gap, 2)} million.`,
      heading:
        gap > 0.05
          ? 'Starts are still running ahead of sales. That is how unsold builder inventory builds, and it is how the next cut to starts gets decided.'
          : 'Starts and sales are close. The inventory build that leads a start cut is not widening in this quarter.',
      forQxo:
        'The gap is builder inventory, not mill shipments. When it stays wide, lumber (Kodiak) feels the later start cut first and Beacon’s new-construction roofing follows. Repair demand does not close this gap.',
    },
    'new-home-mos': {
      where: levelSentence('Months of new-home supply', mos, 1, ''),
      heading:
        mos.latest >= 8
          ? 'Above about 8 months, builders have more houses than a normal market clears. Starts can look fine versus last year and still be too high for the inventory.'
          : mos.latest >= 6
            ? 'Supply is above the old 6-month rule of thumb. That is loose enough to cap how fast starts can rise.'
            : 'Supply is back toward a tight market. That is the condition under which starts are allowed to rise.',
      forQxo:
        'This is the leading read for Kodiak and for Beacon’s new-construction slice. It is months, not homes, which is why it is not drawn on the sales chart. It says nothing about reroof.',
    },
    'housing-backlog': {
      where: `${levelSentence('Homes under construction', under, 2, ' million')} Permitted but not started: ${num(notStarted.latest, 2)} million (${band(notStarted.percentile)}).`,
      heading: headingFromDelta(
        under,
        'The under-construction pile has grown over four quarters. That is work already in the field for installers.',
        'The under-construction pile is shrinking. That is the next step down for installer crews after starts have already cooled.',
        'The backlog is stable. Installers are working through a pile that is no longer growing.',
      ),
      forQxo:
        'Under construction is work TopBuild and other installers are already on. Authorized-not-started can still be cancelled if rates stay high. A shrinking pile shows up in install revenue before it shows up in a new start.',
    },
    'put-in-place': {
      where: levelSentence('Residential construction spending', resi, 0, ' billion dollars, annualized'),
      heading: headingFromDelta(
        resi,
        'Residential dollars have risen over four quarters. Dollars can rise on price while square footage does not.',
        'Residential dollars are falling. That is contractors spending less, which is closer to orders than a start count is.',
        'Residential dollars are flat. Flat dollars with higher material prices usually means fewer units.',
      ),
      forQxo:
        'Dollar spending is what contractors buy against. Residential dollars are the steep-slope book; nonresidential dollars are low-slope and TopBuild commercial. A high dollar level is not healthy square footage if prices did the work.',
    },
    'input-ppi': {
      where: `${levelSentence('Asphalt, the mill feedstock', asphalt, 0, ' on a Q1 2019 = 100 index')} Shingles are ${num(shingles.latest, 0)} and lumber is ${num(lumber.latest, 0)} in the same quarter.`,
      heading:
        asphalt.latest - shingles.latest > 15
          ? 'Feedstock is still well above what mills charge for shingles, on this index. That is the price/cost squeeze. Lumber has its own cycle and should not be read as the shingle story.'
          : 'The gap between asphalt and shingle prices has narrowed on this index. Lumber is a separate book — Kodiak’s — and its spike in 2021 already mean-reverted.',
      forQxo:
        'Distributor gross margin is the gap between what mills charge and what branches charge, plus rebates. Asphalt ripping higher while shingle prices lag shows up a quarter later in adjusted gross margin. Lumber at a calmer index is the Kodiak cost base, not Beacon’s.',
    },
    'branch-cost': {
      where: `${levelSentence('Diesel', diesel, 0, ' on a Q1 2019 = 100 index')} Truck freight is ${num(freight.latest, 0)} (${band(freight.percentile)}).`,
      heading: headingFromDelta(
        diesel,
        'Diesel has moved up over four quarters. That is a delivery-cost headwind on top of product cost.',
        'Diesel has eased over four quarters. Easing helps delivered orders, but the index can still sit far above 2019.',
        'Diesel is flat recently. Flat and elevated is still a higher cost base than 2019.',
      ),
      forQxo:
        'This sits under the product margin. It hits yard and delivery cost, which shows up in gross margin on delivered orders and in SG&A. It is not an ARMA square count. The 2019 base is the right comparison because Beacon’s old cost structure was built closer to that world than to the 2022 spike.',
    },
    'construction-labor': {
      where: levelSentence('Construction payroll growth', payrolls, 1, '% versus a year ago'),
      heading: headingFromDelta(
        payrolls,
        'Payroll growth has reaccelerated over four quarters. Crews are still being added.',
        'Payroll growth is cooling. Openings usually roll over first; payrolls confirm it later. Cooling payroll growth is the later confirmation.',
        'Payroll growth is steady. Steady and low means the industry is no longer hiring its way through a boom.',
      ),
      forQxo:
        'Installer capacity is the constraint on TopBuild and on Beacon’s contractor customers. Openings cooling before payrolls means crews are still on site but hiring has stopped. The next step, if it comes, is fewer jobs and then less material.',
    },
  };
}

function shortRead(id: string): SeriesRead | null {
  const forQxo = SHORT_QXO[id];
  if (!forQxo) return null;
  const latestLike = BEACON_LIKE_FOR_LIKE[BEACON_LIKE_FOR_LIKE.length - 1];
  const latestQuarter = QXO_QUARTERS[QXO_QUARTERS.length - 1];
  const beacon = PLATFORM_BASELINE.find((row) => row.platform.startsWith('Beacon'));
  const extra =
    id === 'like-for-like' && latestLike
      ? ` Latest row ${latestLike.period}: sales ${signedPct(latestLike.salesYoyPct)} versus the Beacon quarter.`
      : id === 'qxo-quarters'
        ? ` Latest reported quarter is ${latestQuarter.period}: $${num(latestQuarter.netSales, 0)} million of sales, ${latestQuarter.adjEbitdaMarginPct == null ? 'adjusted EBITDA margin not in this row' : `${num(latestQuarter.adjEbitdaMarginPct, 1)}% adjusted EBITDA margin`}.`
        : id === 'platforms' && beacon
          ? ` Beacon FY2025 baseline in this file is $${num(beacon.fy25AdjEbitda, 0)} million of adjusted EBITDA against a 2030 target of $${num(beacon.target2030EbitdaB, 1)} billion.`
          : '';
  return {
    where: `This series is only as long as the public record in the table. Stretching it back 10 or 15 years would mean inventing figures.${extra}`,
    heading: cycleBackdrop(),
    forQxo,
  };
}

const LONG = longReads();

export function seriesRead(id: string): SeriesRead | null {
  return LONG[id] ?? shortRead(id);
}
